// src/views/Settings/StatusCodes/AddStatusCodeModal.tsx
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  IconButton,
  Box,
  CircularProgress,
  Divider,
  Autocomplete,
  Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CodeIcon from '@mui/icons-material/Code';
import {
  type CreateStatusCodePayload,
  type MakeModelRecord,
  type MakeModelInput,
} from './types';

const PRESET_COLORS: Array<{ hex: string; nameKey: string }> = [
  { hex: '#16a34a', nameKey: 'sc_color_green' },
  { hex: '#eab308', nameKey: 'sc_color_yellow' },
  { hex: '#dc2626', nameKey: 'sc_color_dark_red' },
  { hex: '#3b82f6', nameKey: 'sc_color_blue' },
];

interface FormFields {
  description: string;
  code: string;
  status: string;
  colorCode: string;
  reasonCategory: string;
}

interface FormErrors {
  description?: string;
  code?: string;
  status?: string;
  colorCode?: string;
  reasonCategory?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onAdd: (payload: CreateStatusCodePayload) => Promise<void>;
  makeModelOptions: MakeModelRecord[];
}

const DEFAULT_FORM: FormFields = {
  description: '',
  code: '',
  status: '',
  colorCode: '#16a34a',
  reasonCategory: '',
};

export const AddStatusCodeModal = ({
  open,
  onClose,
  onAdd,
  makeModelOptions,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormFields>(DEFAULT_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [pairs, setPairs] = useState<MakeModelInput[]>([]);
  const [selectedMake, setSelectedMake] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useState<string | null>(null);
  useEffect(() => {
    if (open) {
      setForm(DEFAULT_FORM);
      setErrors({});
      setPairs([]);
      setSelectedMake(null);
      setSelectedModel(null);
    }
  }, [open]);

  const validate = (): boolean => {
    const next: FormErrors = {};
    if (!form.description.trim()) next.description = t('sc_required');
    const codeNum = Number(form.code);
    if (!form.code.trim() || isNaN(codeNum) || !Number.isInteger(codeNum)) {
      next.code = t('sc_required');
    }
    if (!form.status.trim()) next.status = t('sc_required');
    if (!form.colorCode.trim()) next.colorCode = t('sc_required');
    if (!form.reasonCategory.trim()) next.reasonCategory = t('sc_required');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const uniqueMakes = Array.from(
    new Set(makeModelOptions.map((o) => o.make))
  ).sort();

  const modelOptions = selectedMake
    ? makeModelOptions
        .filter((o) => o.make?.toLowerCase() === selectedMake?.toLowerCase())
        .filter(
          (o) =>
            !pairs.some(
              (p) =>
                p.make?.toLowerCase() === o.make?.toLowerCase() &&
                p.model?.toLowerCase() === o.model?.toLowerCase()
            )
        )
        .map((o) => o.model)
    : [];

  const removePair = (index: number) => {
    setPairs((prev) => prev.filter((_, i) => i !== index));
  };

  const setField =
    (key: keyof FormFields) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [key]: e.target.value }));
      setErrors((prev) => ({ ...prev, [key]: undefined }));
    };

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      await onAdd({
        description: form.description.trim(),
        code: Number(form.code),
        status: form.status.trim(),
        colorCode: form.colorCode,
        reasonCategory: form.reasonCategory.trim(),
        makeModels: pairs,
      });
    } catch {
      // error toasted by parent; modal stays open
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: (th) => alpha(th.palette.primary.main, 0.15),
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          pt: 2.5,
          pb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: (th) => alpha(th.palette.primary.main, 0.12),
              border: '1px solid',
              borderColor: (th) => alpha(th.palette.primary.main, 0.25),
            }}
          >
            <CodeIcon sx={{ fontSize: 18, color: 'primary.main' }} />
          </Box>
          <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
            {t('sc_add_title')}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 1, pb: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label={t('sc_field_code')}
            fullWidth
            size="small"
            type="number"
            value={form.code}
            onChange={setField('code')}
            error={!!errors.code}
            helperText={errors.code}
          />
          <TextField
            label={t('sc_field_status')}
            fullWidth
            size="small"
            value={form.status}
            onChange={setField('status')}
            error={!!errors.status}
            helperText={errors.status}
          />
          <TextField
            label={t('sc_field_description')}
            fullWidth
            size="small"
            value={form.description}
            onChange={setField('description')}
            error={!!errors.description}
            helperText={errors.description}
          />

          {/* Color Code */}
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 1, display: 'block' }}
            >
              {t('sc_field_color_code')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                {PRESET_COLORS.map(({ hex, nameKey }) => (
                  <Tooltip key={hex} title={t(nameKey)} placement="top" arrow>
                    <Box
                      onClick={() => {
                        setForm((prev) => ({ ...prev, colorCode: hex }));
                        setErrors((prev) => ({
                          ...prev,
                          colorCode: undefined,
                        }));
                      }}
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '6px',
                        bgcolor: hex,
                        cursor: 'pointer',
                        border: '2px solid',
                        borderColor:
                          form.colorCode === hex
                            ? 'text.primary'
                            : 'transparent',
                        '&:hover': { opacity: 0.8 },
                      }}
                    />
                  </Tooltip>
                ))}
              </Box>
            </Box>
            {errors.colorCode && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 0.5, display: 'block' }}
              >
                {errors.colorCode}
              </Typography>
            )}
          </Box>

          <TextField
            label={t('sc_field_reason_category')}
            fullWidth
            size="small"
            value={form.reasonCategory}
            onChange={setField('reasonCategory')}
            error={!!errors.reasonCategory}
            helperText={errors.reasonCategory}
          />

          <Divider sx={{ my: 0.5 }} />
          <Typography variant="subtitle2" fontWeight={700} color="text.primary">
            {t('sc_make_models_section')}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Autocomplete<string>
              options={uniqueMakes}
              value={selectedMake}
              onChange={(_, val) => {
                setSelectedMake(val);
                setSelectedModel(null);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label={t('sc_field_make')}
                />
              )}
              sx={{ flex: 1 }}
            />
            <Autocomplete<string>
              options={modelOptions}
              value={selectedModel}
              disabled={!selectedMake}
              onChange={(_, val) => {
                if (selectedMake && val) {
                  setPairs((prev) => [
                    ...prev,
                    { make: selectedMake, model: val },
                  ]);
                  setSelectedMake(null);
                  setSelectedModel(null);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label={t('sc_field_model')}
                />
              )}
              sx={{ flex: 1 }}
            />
          </Box>

          {pairs.map((p, i) => (
            <Box
              key={`${p.make}-${p.model}-${i}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 1.5,
                py: 0.75,
                borderRadius: 1.5,
                bgcolor: (th) => alpha(th.palette.primary.main, 0.05),
                border: '1px solid',
                borderColor: (th) => alpha(th.palette.primary.main, 0.12),
              }}
            >
              <Typography variant="body2">
                {p.make}{' '}
                <Typography
                  component="span"
                  variant="body2"
                  color="text.secondary"
                >
                  {p.model}
                </Typography>
              </Typography>
              <IconButton
                size="small"
                onClick={() => {
                  removePair(i);
                }}
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: 'error.main' },
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1,
          borderTop: '1px solid',
          borderColor: (th) => alpha(th.palette.divider, 0.5),
        }}
      >
        <Button
          variant="outlined"
          color="inherit"
          onClick={handleClose}
          disabled={submitting}
          sx={{ borderRadius: '8px', fontWeight: 600 }}
        >
          {t('sc_cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            void handleSubmit();
          }}
          disabled={submitting}
          startIcon={
            submitting ? (
              <CircularProgress size={14} color="inherit" />
            ) : undefined
          }
          sx={{ borderRadius: '8px', fontWeight: 600, px: 3 }}
        >
          {t('sc_add_submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
